import { format } from "date-fns";

interface CuttingItem {
  orderNumber: string;
  componentName: string | null;
  materialName: string | null;
  materialSku: string | null;
  materialCategory: string | null;
  quantity: number;
  /** Width in meters (multiply by 1000 for mm) */
  width: number;
  /** Height in meters (multiply by 1000 for mm) */
  height: number;
  /** Thickness in meters (multiply by 1000 for mm) */
  thickness: number;
  notes: string | null;
  source: string;
  sourceModelName?: string | null;
  productModelName?: string | null;
}

interface PrintableCuttingListProps {
  title?: string;
  items: CuttingItem[];
  filterOrderNumber?: string;
}

/** Convert meters to mm string for display */
function toMm(meters: number | null | undefined): string {
  if (meters == null) return "-";
  return `${Math.round(meters * 1000)} mm`;
}

export function PrintableCuttingList({
  title = "Sahauslista",
  items,
  filterOrderNumber,
}: PrintableCuttingListProps) {
  const displayTitle = filterOrderNumber 
    ? `${title} - ${filterOrderNumber}` 
    : title;

  return (
    <div className="p-8 bg-white text-black min-h-screen font-sans">
      {/* Header */}
      <div className="border-b-2 border-black pb-4 mb-6">
        <h1 className="text-2xl font-bold">{displayTitle}</h1>
        <div className="mt-2 text-sm text-gray-600">
          Yhteensä {items.length} materiaalia
        </div>
      </div>

      {/* Materials table */}
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-black">
            <th className="text-left py-2">Tilaus</th>
            <th className="text-left py-2">Komponentti</th>
            <th className="text-left py-2">Materiaali</th>
            <th className="text-right py-2">Kpl</th>
            <th className="text-right py-2">Leveys</th>
            <th className="text-right py-2">Korkeus</th>
            <th className="text-right py-2">Paksuus</th>
            <th className="text-left py-2 w-20">Tehty</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={index} className="border-b border-gray-300">
              <td className="py-3 font-medium">{item.orderNumber}</td>
              <td className="py-3">
                {(item.sourceModelName || item.productModelName) && (
                  <div className="text-[10px] uppercase tracking-wide text-gray-400">
                    {item.sourceModelName || item.productModelName}
                  </div>
                )}
                {item.componentName || "-"}
                {item.notes && (
                  <div className="text-xs text-gray-500">{item.notes}</div>
                )}
              </td>
              <td className="py-3">
                {item.materialName || "-"}
                {item.materialSku && (
                  <div className="text-xs text-gray-500">{item.materialSku}</div>
                )}
              </td>
              <td className="py-3 text-right font-medium">{item.quantity}</td>
              <td className="py-3 text-right">{toMm(item.width)}</td>
              <td className="py-3 text-right">{toMm(item.height)}</td>
              <td className="py-3 text-right">{toMm(item.thickness)}</td>
              <td className="py-3">
                <div className="w-6 h-6 border-2 border-black rounded"></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t text-xs text-gray-500">
        Tulostettu: {format(new Date(), "d.M.yyyy HH:mm")}
      </div>
    </div>
  );
}

export function openPrintableCuttingList(props: PrintableCuttingListProps) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Ponnahdusikkuna estetty. Salli ponnahdusikkunat tulostaaksesi.");
    return;
  }

  const displayTitle = props.filterOrderNumber 
    ? `${props.title || "Sahauslista"} - ${props.filterOrderNumber}` 
    : props.title || "Sahauslista";

  /** Convert meters to mm string for display */
  const toMm = (meters: number | null | undefined): string => {
    if (meters == null) return "-";
    return `${Math.round(meters * 1000)} mm`;
  };

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${displayTitle}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; }
        .container { padding: 24px; }
        .header { border-bottom: 2px solid black; padding-bottom: 16px; margin-bottom: 24px; }
        .header h1 { font-size: 24px; font-weight: bold; margin-bottom: 4px; }
        .header .subtitle { font-size: 14px; color: #6b7280; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { text-align: left; padding: 8px 4px; border-bottom: 2px solid black; font-weight: 600; }
        th.right { text-align: right; }
        td { padding: 10px 4px; border-bottom: 1px solid #d1d5db; vertical-align: top; }
        td.right { text-align: right; }
        .sku { font-size: 10px; color: #6b7280; }
        .notes { font-size: 10px; color: #6b7280; font-style: italic; }
        .model-name { font-size: 9px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em; }
        .checkbox { width: 18px; height: 18px; border: 2px solid black; border-radius: 3px; }
        .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .container { padding: 0; }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>${displayTitle}</h1>
          <div class="subtitle">Yhteensä ${props.items.length} materiaalia</div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Tilaus</th>
              <th>Komponentti</th>
              <th>Materiaali</th>
              <th class="right">Kpl</th>
              <th class="right">Leveys</th>
              <th class="right">Korkeus</th>
              <th class="right">Paksuus</th>
              <th style="width: 50px;">Tehty</th>
            </tr>
          </thead>
          <tbody>
            ${props.items.map((item) => `
              <tr>
                <td style="font-weight: 500;">${item.orderNumber}</td>
                <td>
                  ${(item.sourceModelName || item.productModelName) ? `<div class="model-name">${item.sourceModelName || item.productModelName}</div>` : ""}
                  ${item.componentName || "-"}
                  ${item.notes ? `<div class="notes">${item.notes}</div>` : ""}
                </td>
                <td>
                  ${item.materialName || "-"}
                  ${item.materialSku ? `<div class="sku">${item.materialSku}</div>` : ""}
                </td>
                <td class="right" style="font-weight: 500;">${item.quantity}</td>
                <td class="right">${toMm(item.width)}</td>
                <td class="right">${toMm(item.height)}</td>
                <td class="right">${toMm(item.thickness)}</td>
                <td><div class="checkbox"></div></td>
              </tr>
            `).join("")}
          </tbody>
        </table>

        <div class="footer">
          Tulostettu: ${format(new Date(), "d.M.yyyy HH:mm")}
        </div>
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
}
